import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, TransferState, effect, inject, makeStateKey, signal } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { LoadingState, ObservableState } from 'src/app/core/constants/observable-state.interface';
import { ListParams } from 'src/app/core/models/list-params.model';
import { UserSession } from 'src/app/core/models/user.model';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { MappedProductListingSearch, ProductListSearch } from '../models/product-listing.model';
import { MappedProductSearch, ProductSearch } from '../models/product-search.model';
import { AiChatReply, CategoryDetail, Collection, Product, ProductList } from '../models/product.model';
import { ExpectedDeliveryDates } from './expected-delivery.service';
import { FiltersAdapterService } from './filters-adapter.service';


export interface ProductFilters {
  search: string;
}

export interface RelatedProduct {
  position: number;
  product: Product;
}

export interface QuotedBid {
  bidding_price: number,
  last_bid_price: number,
  our_quoted_price?: number,
  bidding_count: number,
  seller_company?: string;
  buyer_company?: string;
}

export interface AvailableBid {
  price: number,
  our_quoted_price: number,
  last_bid_price: number,
  is_low_bid: boolean
  is_acceptable: boolean
}

@Injectable({
  providedIn: 'root'
})
export class ProductsApiService {
  private http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);
  private filtersAdapterService = inject(FiltersAdapterService);
  private userService = inject(UserService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private transferState = inject(TransferState)

  // Use reactive signals for URLs that automatically update when environment changes
  private _url = signal<string>('');
  private _url_2 = signal<string>('');
  private _product_url = signal<string>('');
  private _product_url_2 = signal<string>('');
  private _url1 = signal<string>('');

  // Public readonly signals
  readonly url = this._url.asReadonly();
  readonly url_2 = this._url_2.asReadonly();
  readonly product_url = this._product_url.asReadonly();
  readonly product_url_2 = this._product_url_2.asReadonly();
  readonly url1 = this._url1.asReadonly();

  readonly showOutOfStockProducts = this.storeConfigurationRepositoryService.showOutOfStockProducts;

  // Get current user from signal-based service
  getCurrentUser(): UserSession {
    return this.userService.appUser;
  }

  // Check if user is authenticated
  isUserAuthenticated(): boolean {
    return this.userService.isAuthenticated() || false;
  }

  constructor() {
    // âœ… SYNCHRONOUS initialization - read environment IMMEDIATELY (it's already loaded by APP_INITIALIZER)
    const environment = this.environmentService.environment();
    if (environment) {
      this._url.set(environment.url || '');
      this._url_2.set(environment.url_2 || '');
      this._product_url.set(environment.product_url || environment.url || '');
      this._product_url_2.set(environment.product_url_2 || environment.url_2 || '');
      this._url1.set(`${this.environmentService.backendBaseUrl}/v2/`);
    }

    // âœ… REACTIVE updates - handle environment changes in the future
    effect(() => {
      const environment = this.environmentService.environment();
      if (environment) {
        this._url.set(environment.url || '');
        this._url_2.set(environment.url_2 || '');
        this._product_url.set(environment.product_url || environment.url || '');
        this._product_url_2.set(environment.product_url_2 || environment.url_2 || '');
        this._url1.set(`${this.environmentService.backendBaseUrl}/v2/`);
      }
    });
  }

  getProducts(params?: ListParams<any>): Observable<MappedProductSearch> {
    const { filter, pageNumber, pageSize, currentFilters } = params || {};
    let _params = new HttpParams({ fromObject: currentFilters || {} })
      .set('size', (pageSize || 20).toString())
      .set('offset', ((pageSize || 20) * (pageNumber || 0)).toString())
    // .set('is_collapse_by_variants', 'true')
    if (filter) {
      _params = _params.set('alphabet_filter', filter);
    }

    // ðŸ” DEBUG: Log API call details for collection-based requests

    return this.http.get<ProductSearch>(`${this.url()}/products`, {
      params: _params
    }).pipe(
      tap((response: ProductSearch) => {
        // Response received successfully
      }),
      map((response: ProductSearch) => {
        const mappedResponse = this.filtersAdapterService.toResponse(response) as MappedProductSearch;
        return mappedResponse;
      })
    );
  }


  getProductsForVersion2(params?: ListParams<any>): Observable<MappedProductListingSearch> {
    const { filter, pageNumber, pageSize, currentFilters } = params || {};
    let _params = new HttpParams({ fromObject: currentFilters || {} })
      .set('size', (pageSize || 20).toString())
      .set('offset', ((pageSize || 20) * (pageNumber || 0)).toString())
      .set('is_collapse_by_variants', 'true')
    if (filter) {
      _params = _params.set('alphabet_filter', filter);
    }

    const apiUrl = `${this.url_2()}/products`;

    return this.http.get<ProductListSearch>(apiUrl, {
      params: _params
    }).pipe(
      tap(() => { }),
      map((response: ProductListSearch) => {
        const mappedResponse = this.filtersAdapterService.toResponseV2(response) as MappedProductListingSearch;
        return mappedResponse;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  getProductsByIds(productIds: string[]): Observable<ProductSearch> {

    let _params: HttpParams = new HttpParams({
      fromObject: {
        product_ids: productIds.join(',')
      }
    });

    if (!this.showOutOfStockProducts()) {
      _params = _params.set('in_stock', true);
    }

    return this.http.get<ProductSearch>(`${this.url()}/products`, {
      params: _params
    });
  }

  getProductsForCarousel(params?: any): Observable<ProductSearch> {
    const { pageNumber, pageSize, currentFilters } = params || {};
    const _params = new HttpParams({ fromObject: currentFilters })
      .set('size', pageSize.toString())
      .set('offset', (pageSize * pageNumber).toString());
    return this.http.get<ProductSearch>(`${this.url()}/products`, {
      params: _params
    });
  }

  getProductsByScrollId(scroll_id: string): Observable<ProductSearch> {
    return this.http.post<ProductSearch>(`${this.url()}/products/scroll`, {
      scroll_id
    });
  }


  getProduct(productId: string, makeNewApiCall = true): Observable<Product> {
    const environment = this.environmentService.environment();

    if (!environment?.sellerId) {
      throw new Error('Environment seller ID not available');
    }

    let params: HttpParams = new HttpParams({
      fromObject: {
        seller: environment?.sellerId ?? ''
      }
    });

    if (makeNewApiCall) {
      params = params.set('time', new Date().getTime());
    }

    return this.http.get<ProductSearch>(`${this.product_url_2()}/products/${productId}`, {
      params
    }).pipe(
      map((response: ProductSearch) => {
        const { hits } = response;
        const [firstHit] = hits.hits;

        if (!firstHit?._source) {
          throw new Error('No product found');
        }

        const product = firstHit._source;

        return product;
      }),
      tap((product) => {
        try {
          const DATA_KEY = makeStateKey<Product>(`product:${productId}`);
          this.transferState.set(DATA_KEY, product);
        } catch { }
      })
    );
  }

  private categoryApiCallCount = 0;

  getProductCategory(category_url_path: string): Observable<CategoryDetail | null> {
    const environment = this.environmentService.environment();

    if (!environment?.sellerId) {
      throw new Error('Environment seller ID not available');
    }

    this.categoryApiCallCount++;
    const apiUrl = `${this.product_url()}/categories/${category_url_path}`;
    const params = {
      seller: environment?.sellerId ?? ''
    };

    return this.http.get<CategoryDetail>(apiUrl, {
      params,
      responseType: 'json'
    }).pipe(
      catchError((error) => {
        return of(null);
      })
    );
  }

  /**
   * 
   * @param filters 
   * @returns 
   * @todo use this to prepare the params for the filtering of the products api
   */
  private prepareQueryParams(filters: ProductFilters): { [param: string]: string | readonly string[] } {
    const { search = '' } = filters || {};
    return ({
      search
      // Removed duplicate search property
    });
  }

  createGetRequestFromUrl<T>(url: string | null): Observable<ObservableState<T, any>> | null {
    // if url is null then request will not be sent
    if (!url) {
      return null;
    }

    const environment = this.environmentService.environment();

    if (!environment?.sellerId) {
      return of({
        error: 'Environment seller ID not available',
        loadingState: LoadingState.Error
      });
    }

    // Add required parameters like other API calls
    const params = new HttpParams({
      fromObject: {
        seller: environment.sellerId,
        time: new Date().getTime().toString()
      }
    });

    const httpRequest = this.http.get<T>(url, { params });

    return httpRequest.pipe(
      map(response => {
        const result = {
          response,
          loadingState: LoadingState.Loaded
        };
        return result;
      }),
      catchError((error) => {
        return of(({
          error,
          loadingState: LoadingState.Error
        }));
      }));
  }

  askChatGpt(prompt: string, productId: string): Observable<AiChatReply> {
    return this.http.post<AiChatReply>(`${this.product_url()}/products/${productId}/chat-gpt`, { prompt })
  }

  sendCustomerBid(productId: string, biddingDetails: QuotedBid): Observable<AvailableBid> {
    const environment = this.environmentService.environment();

    if (!environment?.sellerId) {
      throw new Error('Environment seller ID not available');
    }

    // Check if user and company are available before making the request
    const currentUser = this.userService.appUser;
    if (!currentUser?.company?.id) {
      throw new Error('User or company information not available');
    }

    biddingDetails = {
      ...biddingDetails,
      seller_company: environment?.sellerId ?? '',
      buyer_company: currentUser.company.id
    };

    return this.http.post<AvailableBid>(`${this.product_url()}/products/${productId}/bidding`, biddingDetails, {
      params: {}
    });
  }

  getExpectedDeliveryDates(productId: string, pincode: string): Observable<ExpectedDeliveryDates> {

    let params = new HttpParams();

    if (pincode) {
      params = params.set('pincode', pincode)
    }

    return this.http.get<ExpectedDeliveryDates>(`${this.product_url()}/products/${productId}/expected_delivery`, {
      params
    })
  }

  getCollectionDetails(collectionId: string): Observable<Collection> {
    const environment = this.environmentService.environment();

    if (!environment?.sellerId) {
      throw new Error('Environment seller ID not available');
    }

    const params: HttpParams = new HttpParams({
      fromObject: {
        seller: environment?.sellerId ?? ''
      }
    });

    return this.http.get<Collection>(`${this.product_url()}/collections/${collectionId}`, {
      params
    });
  }

  createGetRequestFromUrlForProductsList<T>(productList: ProductList | null): Observable<ObservableState<T, any>> | null {

    const { url, count = 0 } = productList || {};

    // if count is 0 or url is undefined then request will not be sent
    if (!count || !url) {
      return null;
    }

    return this.http.get<T>(url).pipe(
      map(response => ({
        response,
        loadingState: LoadingState.Loaded
      })),
      catchError((error) => {
        return of(({
          error,
          loadingState: LoadingState.Error
        }));
      }),
      startWith({
        response: undefined,
        loadingState: LoadingState.Loading
      }));
  }

}
